import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SiteHeader } from '@/components/_vs/marketing/SiteHeader';
import { BrandLogo } from '@/components/_vs/marketing/BrandLogo';
import { isDarkColor } from '@/components/_vs/marketing/colorContrast';
import { LanguageSwitcher } from '@/components/_vs/i18n/LanguageSwitcher';
import { ThemeToggle } from '@/components/_vs/nav/ThemeToggle';
import { LOCALES, DEFAULT_LOCALE, localizedPath, parseLocalizedPath } from '@/i18n/config';
import { getNavItems } from '@/utils/nav';
import { getLenis } from '@/utils/lenis';
import settings from '@/content/settings.json';
import pages from '@/content/pages.json';

export function Header({ locale = DEFAULT_LOCALE }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { slug } = parseLocalizedPath(pathname);
  const onHome = slug === '/';
  const homeHref = localizedPath('/', locale);

  // "Testo chiaro sopra l'hero" ha senso solo se la pagina apre davvero con
  // un hero (sfondo scuro/foto) — su pagine senza un blocco in testa che
  // fornisca contrasto (es. pagine legali, tutto testo su sfondo chiaro) il
  // branding dell'header deve restare sempre leggibile, non solo da scrollato.
  const firstBlockType = pages.find((p) => p.slug === slug)?.sections?.[0]?.type;
  const hasHeroAtTop = firstBlockType === 'hero' || firstBlockType === 'heroSlideshow';
  const lightTextOnHero = hasHeroAtTop && settings.header.lightTextOnHero;

  // Il logo (immagine, non testo) non può seguire il cambio colore via CSS
  // di SiteHeader (--color-text non recolora un <img src="logo.svg">) —
  // replica qui la stessa logica chiaro/scuro di SiteHeader.jsx per scegliere
  // la variante giusta (logo.svg / logo-dark.svg) allo stesso momento in cui
  // cambia il resto del testo dell'header.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Da scrollato, se non c'è un bgColor esplicito da admin, l'header prende
  // lo sfondo del tema (SiteHeader.jsx: color-mix con --color-bg) — che è
  // scuro quando il sito è in dark mode (ThemeToggle, data-mode="dark" su
  // <html>). Senza controllarlo, il logo restava sempre la variante nera
  // anche su un header scrollato scuro per via del dark mode (non di un
  // bgColor impostato), diventando illeggibile. Osservato via MutationObserver
  // invece che letto una volta sola: il toggle lo cambia senza remount.
  const [colorMode, setColorMode] = useState(() => document.documentElement.getAttribute('data-mode') || 'light');
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setColorMode(document.documentElement.getAttribute('data-mode') || 'light');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mode'] });
    return () => observer.disconnect();
  }, []);

  const customDark = scrolled && (
    settings.header.bgColor ? isDarkColor(settings.header.bgColor) : colorMode === 'dark'
  );
  const logoOnDark = (lightTextOnHero && !scrolled) || customDark;

  // Il pannello mobile a tutto schermo (SiteHeader.jsx) non è mai sopra
  // l'hero: ha sempre lo sfondo "piatto" del tema (bg-theme-bg), chiaro o
  // scuro solo in base alla dark mode del sito, mai per lightTextOnHero/
  // bgColor scrollato (quelli riguardano solo la fase overlay dell'header).
  const panelLogoOnDark = colorMode === 'dark';

  // Il logo porta sempre alla home — se ci si è già, scrolla in cima invece
  // di una navigazione a vuoto (stesso URL, nessun cambio di location).
  function handleLogoClick(e) {
    if (!onHome) return;
    e.preventDefault();
    const lenisInstance = getLenis();
    if (lenisInstance) lenisInstance.scrollTo(0, { duration: 1 });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <SiteHeader
      overlay
      dark={lightTextOnHero}
      logo={
        <Link to={homeHref} onClick={handleLogoClick} aria-label="Torna alla home">
          <BrandLogo
            settings={settings.header}
            fallbackText="Morettino Caffè Palermo"
            dark={logoOnDark}
            height={settings.header.logoHeight ?? 44}
            maxWidth={settings.header.logoMaxWidth || undefined}
          />
        </Link>
      }
      mobileMenuLogo={
        <Link to={homeHref} onClick={handleLogoClick} aria-label="Torna alla home">
          <BrandLogo
            settings={settings.header}
            fallbackText="Morettino Caffè Palermo"
            dark={panelLogoOnDark}
            height={settings.header.logoHeight ?? 44}
            maxWidth={settings.header.logoMaxWidth || undefined}
          />
        </Link>
      }
      bgColor={settings.header.bgColor}
      uppercase={settings.header.linksUppercase}
      linkAs={Link}
      links={getNavItems(pages, locale).map((item) => ({
        href: item.href,
        label: item.label,
        active: !item.isAnchor && item.slug === slug,
      }))}
      rightSlot={
        <div className="flex items-center gap-2">
          <ThemeToggle variant="icon" />
          <LanguageSwitcher locale={locale} locales={LOCALES} onChange={(code) => navigate(localizedPath(slug, code))} />
        </div>
      }
    />
  );
}
